<template>
  <CollapsableSection
    data-test="documentation"
    :title="t('documentation')"
    section-classes="w-full text-xs text-gray-800 border-b border-gray-200 border-solid last:border-0"
    title-classes="lg:text-xl"
    :is-open="isOpen"
    collapsable-padding="px-8 xl:px-12 py-4"
    collapsable-content-classes="px-8 xl:px-12 py-6"
    @update:open="(value) => emit('update:open', value)"
  >
    <div class="w-full md:grid md:grid-cols-2 md:gap-x-4 lg:gap-x-6">
      <div v-for="(section, index) in sections" :key="index">
        <template v-if="index === 0 && product && displayPrintBarcodeButton">
          <div :ref="(el) => (printAreaRef[index] = el)" class="hidden">
            <BarCodes :articles="articles" />
          </div>
          <button class="flex mb-2" @click="print">
            <CIcon class="mr-2" :name="'print'" />
            <span>{{ t('print_barcode') }}</span>
          </button>
        </template>
        <a
          v-for="(document, documentIndex) in section"
          :key="`doc-${documentIndex}`"
          :data-test="`product-doc-${document.assetType}`"
          :href="document.assetUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex mb-2"
        >
          <CIcon class="mr-2" :name="document.icon" />
          <span>{{ document.assetLabel || document.assetType }}</span>
        </a>
      </div>
    </div>
  </CollapsableSection>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useBrandStore } from '@/stores';
import { printContent } from '@/utils/Print';
import type { Price, Variant } from '@/types/products';

type Props = {
  isOpen?: boolean;
  documents: {
    assetUrl: string;
    assetType: string;
    icon: string;
    assetLabel: string;
  }[];
};

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  documents: () => [],
});

const emit = defineEmits<{
  (e: 'update:open', v: boolean): void;
}>();

const printAreaRef = ref<any[]>([]);

const { t } = useI18n();
const { mq } = useMediaQueries();
const { brandConfig } = useBrandStore();
const displayPrintBarcodeButton = brandConfig.additions.displayPrintBarcodeButton;
const product = inject<{ value: { name: string; requestedVariant: Variant } }>('product', {
  value: {
    name: '',
    requestedVariant: {
      name: '',
      articleNumber: undefined,
      price: {},
      webSalesUnit: {},
    },
  },
});

const isMultiSection = computed(() => {
  if (['xsmall', 'small'].includes(mq.value?.current || '')) return false;
  return props.documents.length > 7;
});

const sections = computed(() => {
  if (!isMultiSection.value) return [props.documents];
  const documents = props.documents;
  const half = Math.ceil(documents.length / 2);
  return [documents.slice(0, half), documents.slice(half)];
});

const articles = computed(() => {
  const { requestedVariant } = product.value;
  if (!requestedVariant) return [];
  const { price, articleNumber, webSalesUnit } = requestedVariant;
  return [
    {
      number: articleNumber as unknown as number,
      name: requestedVariant.name,
      unit: webSalesUnit?.unit || (price as Price)?.salesPriceUnit,
    },
  ];
});

const print = () => {
  const { name } = product.value;
  printContent(printAreaRef.value[0], { title: name });
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductDetailPageDocumentsSection',
});
</script>
